import React, { useMemo, useState } from 'react';
import { Button, Input, Select, Table, Switch, Space, Modal, message, Tag } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Display } from '../../../CRM/organism/Main/styles';
import { useToken } from '../../../app/hooks';
import { useAdminPopupList } from '../../hooks/Admin/PopupManage/useAdminPopupList';
import PopupFormModal from './PopupFormModal';
import {
  createPopup,
  deletePopup,
  fetchPopupDetail,
  PopupFormPayload,
  updatePopup,
} from './api';

const { Option } = Select;

type StatusFilter = 'all' | 'active' | 'inactive';
const OPTIONAL_TEXT_PLACEHOLDER = '\u200B'; // matches placeholder used when saving optional text
const stripPlaceholder = (value: string) => value.split(OPTIONAL_TEXT_PLACEHOLDER).join('');

const PopupManage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [keyword, setKeyword] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);

  const { data: token } = useToken();
  const authToken = useMemo(() => (token ? `${token}` : ''), [token]);

  const {
    popupList,
    total,
    isLoading,
    mutatePopupList,
  } = useAdminPopupList(page, pageSize, status, keyword);

  const handleSearch = () => {
    setKeyword(searchValue.trim());
    setPage(1);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setModalInitialValues(null);
    setModalOpen(true);
  };

  const openEditModal = async (popupId: number) => {
    if (!authToken) {
      message.error('인증 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }
    setDetailLoading(true);
    try {
      const detail = await fetchPopupDetail(authToken, popupId);
      if (!detail) throw new Error();
      setEditingId(popupId);
      setModalInitialValues({
        ...detail,
        isActive: Boolean(detail.isActive),
      });
      setModalOpen(true);
    } catch (error) {
      console.error(error);
      message.error('팝업 정보를 불러오지 못했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmit = async (payload: PopupFormPayload) => {
    if (!authToken) {
      message.error('인증 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }
    setModalLoading(true);
    try {
      if (editingId) {
        await updatePopup(authToken, editingId, payload);
        message.success('팝업을 수정했습니다.');
      } else {
        await createPopup(authToken, payload);
        message.success('팝업을 등록했습니다.');
      }
      setModalOpen(false);
      await mutatePopupList();
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || '처리에 실패했습니다.');
    } finally {
      setModalLoading(false);
    }
  };

  const sanitizeText = (value?: string | null) => {
    if (typeof value !== 'string') return OPTIONAL_TEXT_PLACEHOLDER;
    const trimmed = stripPlaceholder(value).trim();
    return trimmed.length ? trimmed : OPTIONAL_TEXT_PLACEHOLDER;
  };

  const handleToggleStatus = async (popupId: number, nextValue: boolean) => {
    if (!authToken) {
      message.error('인증 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }
    setStatusUpdatingId(popupId);
    try {
      const detail = await fetchPopupDetail(authToken, popupId);
      if (!detail) throw new Error('팝업 정보를 찾을 수 없습니다.');
      const payload: PopupFormPayload = {
        title: sanitizeText(detail.title),
        body: sanitizeText(detail.body),
        imageUrl: detail.imageUrl ?? null,
        linkUrl: detail.linkUrl ?? null,
        linkLabel: detail.linkLabel ?? null,
        isActive: nextValue,
        showFrom: detail.showFrom,
        showUntil: detail.showUntil,
        displayOrder: detail.displayOrder ?? 0,
      };
      await updatePopup(authToken, popupId, payload);
      message.success('노출 상태를 변경했습니다.');
      await mutatePopupList();
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || '노출 상태 변경에 실패했습니다.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDelete = (popupId: number) => {
    if (!authToken) {
      message.error('인증 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }

    Modal.confirm({
      title: '선택한 팝업을 삭제하시겠습니까?',
      content: '삭제 후에는 복구할 수 없습니다.',
      okText: '삭제',
      cancelText: '취소',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          await deletePopup(authToken, popupId);
          message.success('팝업을 삭제했습니다.');
          await mutatePopupList();
        } catch (error: any) {
          console.error(error);
          message.error(error?.message || '팝업 삭제에 실패했습니다.');
        }
      },
    });
  };

  const columns = [
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      render: (value: string | null) => {
        const display = typeof value === 'string' ? stripPlaceholder(value).trim() : '';
        return <span>{display || '-'}</span>;
      },
    },
    {
      title: '노출',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_: any, record: any) => (
        <Switch
          checkedChildren="노출"
          unCheckedChildren="숨김"
          checked={Boolean(record.isActive)}
          loading={statusUpdatingId === record.popupId}
          onChange={(checked) => handleToggleStatus(record.popupId, checked)}
        />
      ),
    },
    {
      title: '노출 기간',
      key: 'period',
      render: (_: any, record: any) => {
        const from = record.showFrom ? dayjs(record.showFrom).format('YYYY-MM-DD HH:mm') : '즉시';
        const until = record.showUntil ? dayjs(record.showUntil).format('YYYY-MM-DD HH:mm') : '상시';
        return (
          <span>{`${from} ~ ${until}`}</span>
        );
      },
    },
    {
      title: '정렬 순서',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      render: (value: number) => value ?? 0,
    },
    {
      title: '업데이트',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '링크',
      key: 'link',
      render: (_: any, record: any) => {
        if (!record.linkUrl) return <Tag color="default">미지정</Tag>;
        return (
          <Tag color="purple">
            {record.linkLabel || '링크'}
          </Tag>
        );
      },
    },
    {
      title: '동작',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record.popupId)}>
            수정
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.popupId)}>
            삭제
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <Display>
      <HeaderSection>
        <div>
          <Input.Search
            placeholder="제목 검색"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            enterButton="검색"
            allowClear
            style={{ width: 320 }}
          />
          <Select
            value={status}
            onChange={(value: StatusFilter) => {
              setStatus(value);
              setPage(1);
            }}
            style={{ width: 140 }}
          >
            <Option value="all">전체</Option>
            <Option value="active">노출중</Option>
            <Option value="inactive">숨김</Option>
          </Select>
        </div>
        <Button type="primary" onClick={openCreateModal}>
          팝업 등록
        </Button>
      </HeaderSection>

      <Table
        rowKey="popupId"
        columns={columns}
        dataSource={popupList}
        loading={isLoading || detailLoading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />

      <PopupFormModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initialValues={modalInitialValues}
        confirmLoading={modalLoading}
      />
    </Display>
  );
};

export default PopupManage;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  & > div {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;
